
import { useMemo, useState } from "react";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMeHistory } from "@/hooks/use-me-queries";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { statusColorClass } from "@/components/attendance/attendance-cell";
import type { CellStatus } from "@/lib/types";
import { todayIST } from "@/lib/timezone";

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

function cellLabel(status: CellStatus, lateMinutes?: number | null): string {
  if (status === "L" && lateMinutes != null) return `L${lateMinutes}`;
  if (status === "pending") return "—";
  return status;
}

export default function MeHistoryPage() {
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseISO(todayIST()))
  );
  const month = monthKey(cursor);
  const { data, isLoading, error } = useMeHistory(month);

  const daysByDate = useMemo(() => {
    const map = new Map<string, { status: CellStatus; late_minutes?: number | null }>();
    data?.days.forEach((d) => map.set(d.date, d));
    return map;
  }, [data]);

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const startPad = firstDay.getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array(startPad).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">History</h2>
        <p className="text-sm text-muted-foreground">
          P = present · L = late · A = absent · O = official leave · S = sick ·
          TP = taken permission · H = holiday
        </p>
      </div>

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={() =>
            setCursor(new Date(year, monthIndex - 1, 1))
          }
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="font-medium">{format(cursor, "MMMM yyyy")}</p>
        <Button
          variant="outline"
          size="icon"
          onClick={() =>
            setCursor(new Date(year, monthIndex + 1, 1))
          }
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading && (
        <p className="text-center text-muted-foreground">Loading…</p>
      )}
      {error && (
        <p className="text-center text-red-600">Could not load history.</p>
      )}

      <div className="rounded-xl border bg-white p-3">
        <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`pad-${idx}`} />;
            }
            const dateStr = format(
              new Date(year, monthIndex, day),
              "yyyy-MM-dd"
            );
            const record = daysByDate.get(dateStr);
            const status = record?.status;

            return (
              <div
                key={dateStr}
                className="flex flex-col items-center gap-0.5 rounded-md p-1"
              >
                <span className="text-xs text-muted-foreground">{day}</span>
                {status ? (
                  <span
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded border text-xs font-bold",
                      statusColorClass(status)
                    )}
                  >
                    {cellLabel(status, record?.late_minutes)}
                  </span>
                ) : (
                  <span className="h-8 w-8" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3 text-xs">
        <Legend label="Present" className="bg-emerald-100 text-emerald-800 border-emerald-200" />
        <Legend label="Late" className="bg-amber-100 text-amber-900 border-amber-200" />
        <Legend label="Absent" className="bg-red-100 text-red-800 border-red-200" />
        <Legend label="Official leave (O)" className="bg-sky-100 text-sky-800 border-sky-200" />
        <Legend label="Sick (S)" className="bg-rose-100 text-rose-800 border-rose-200" />
        <Legend label="Permission (TP)" className="bg-teal-100 text-teal-800 border-teal-200" />
        <Legend label="Holiday (H)" className="bg-violet-100 text-violet-800 border-violet-200" />
        <Legend label="Pending" className="bg-slate-100 text-slate-500 border-slate-200" />
      </div>
    </div>
  );
}

function Legend({ label, className }: { label: string; className: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-block h-4 w-4 rounded border", className)} />
      {label}
    </span>
  );
}
